// Boot-time SW registration + main-thread relay between the SW and
// the vault worker. Idempotent: safe to call from React StrictMode
// and from auto-reload.
//
// On every navigation, the SW is registered at scope "/" and the
// `navigator.serviceWorker.message` listener is attached once. The
// SW posts `{type: 'vault-stream-request', token, rangeStart,
// rangeEndExclusive, requestId}` with port1 transferred; we forward
// the request to the vault worker and post the result back on port1.
//
// On rapid video seek the browser aborts stale Range fetches; the SW
// raises `{type: 'vault-stream-cancel', requestId}` (no port) so the
// relay can tell the worker to stop pulling chunks for the superseded
// request.

import type { Remote } from "comlink";
import type { VaultWorkerApi } from "../worker/api";

let registered = false;
let listenerAttached = false;

/**
 * Resolves to `true` once a Service Worker is actively controlling the
 * current page (or `false` if the platform has no SW support / the SW
 * never claims within a generous bound).
 *
 * Use this from any code path that needs to commit to the SW URL
 * scheme — synchronous `navigator.serviceWorker.controller` checks
 * race the SW activation on first hard load.
 */
export async function vaultStreamSwReady(timeoutMs = 2000): Promise<boolean> {
  if (typeof navigator === "undefined") return false;
  const sw = navigator.serviceWorker;
  if (!sw) return false;
  if (sw.controller) return true;
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    sw.addEventListener(
      "controllerchange",
      () => {
        clearTimeout(timer);
        resolve(sw.controller != null);
      },
      { once: true },
    );
  });
}

export async function registerVaultStreamSw(
  vault: Remote<VaultWorkerApi>,
): Promise<void> {
  if (!("serviceWorker" in navigator)) return;

  if (!registered) {
    try {
      // Build emits to /vault-stream-sw.js at site root. Scope "/"
      // is required so fetches under /__vault/stream/* are claimed.
      await navigator.serviceWorker.register("/vault-stream-sw.js", {
        scope: "/",
        type: "module",
      });
      await navigator.serviceWorker.ready;
      registered = true;
    } catch (err) {
      console.warn("vault-stream-sw registration failed", err);
      return;
    }
  }

  if (!listenerAttached) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event.data as
        | {
            type: "vault-stream-request";
            token: string;
            rangeStart: number;
            rangeEndExclusive: number | null;
            requestId: string;
          }
        | { type: "vault-stream-cancel"; requestId: string }
        | { type: string };

      if (data?.type === "vault-stream-cancel") {
        const requestId = (data as { requestId?: unknown }).requestId;
        // Mirror the stream-request validation — a non-string truthy
        // value would otherwise get into the worker's inflight map as
        // a non-string key and break cancellation correlation.
        if (typeof requestId !== "string" || requestId.length === 0) return;
        void vault.abortStreamRequest(requestId).catch(() => {
          // Best-effort; aborting an already-completed request is a no-op.
        });
        return;
      }

      if (data?.type !== "vault-stream-request") return;
      const port = event.ports[0];
      if (!port) return;
      const { token, rangeStart, rangeEndExclusive, requestId } = data as {
        token: string;
        rangeStart: number;
        rangeEndExclusive: number | null;
        requestId: string;
      };
      // Guard against malformed or rollout-skew messages (e.g. an
      // older SW deployment posting without the requestId field).
      // Forwarding an `undefined` key into the worker's inflight map
      // would break cancellation correlation and pollute the map.
      if (typeof requestId !== "string" || requestId.length === 0) {
        port.postMessage({ ok: false, error: "missing requestId" });
        return;
      }
      vault
        .handleStreamRequest(token, rangeStart, rangeEndExclusive, requestId)
        .then((payload) => {
          port.postMessage({ ok: true, payload }, [payload.bytes.buffer]);
        })
        .catch((err) => {
          // Always settle the port-reply handshake — even on abort,
          // the SW's `replyPromise` must reject so the fetch handler
          // can release its event lifetime. Tagging AbortError
          // explicitly lets the SW distinguish user-visible errors
          // from cooperative cancellation.
          const error =
            (err as { name?: string })?.name === "AbortError"
              ? "AbortError"
              : String(err);
          port.postMessage({ ok: false, error });
        });
    });
    listenerAttached = true;
  }
}
